import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FileText, MapPin, Users, Shield, UserCheck, Briefcase, Users2, FolderOpen, Bell,
  Edit3, Save, X, Plus, Trash2, Loader2, Check, Minus,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import {
  willStatusAPI, poaHoldersAPI, otherAdvisorsAPI, formalArrangementsAPI, childrenAPI,
} from '../services/api';
import { toast } from 'sonner';

// ── Constants ────────────────────────────────────────────────────────────
const HELD_WITH_OPTIONS = [
  { value: 'lawyer', label: 'Lawyer / Law Firm' },
  { value: 'self', label: 'Myself' },
  { value: 'bank', label: 'Bank / Safe Deposit' },
  { value: 'digital', label: 'Digital Storage' },
  { value: 'other', label: 'Other' },
];
const POA_TYPE_OPTIONS = [
  { value: 'financial', label: 'Financial' },
  { value: 'medical', label: 'Medical / Healthcare' },
  { value: 'both', label: 'Both' },
  { value: 'enduring', label: 'Enduring / Lasting' },
  { value: 'other', label: 'Other' },
];
const ADVISOR_TYPE_OPTIONS = [
  { value: 'Accountant', label: 'Accountant' },
  { value: 'Financial Advisor', label: 'Financial Advisor' },
  { value: 'Tax Advisor', label: 'Tax Advisor' },
  { value: 'Other', label: 'Other' },
];

const FORM_SECTIONS = ['willDetails', 'location', 'executors', 'poa', 'legalAdvisor', 'review'];

// The 8 content sections shown as tiles (review is a maintenance strip, not a tile).
const SECTION_ORDER = ['willDetails', 'location', 'executors', 'poa', 'poaHolders', 'legalAdvisor', 'advisors', 'arrangements'];

const SECTION_META = {
  willDetails: { icon: FileText, title: 'Will Details' },
  location: { icon: MapPin, title: 'Will Location & Holder' },
  executors: { icon: Users, title: 'Executors' },
  poa: { icon: Shield, title: 'Power of Attorney' },
  poaHolders: { icon: UserCheck, title: 'POA Holders' },
  legalAdvisor: { icon: Briefcase, title: 'Legal Advisor' },
  advisors: { icon: Users2, title: 'Other Advisors' },
  arrangements: { icon: FolderOpen, title: 'Formal Arrangements' },
};

const TILE_GROUPS = [
  { name: 'The Will', keys: ['willDetails', 'location', 'executors'] },
  { name: 'Ongoing Authority', keys: ['poa', 'poaHolders'] },
  { name: 'Advisors & Arrangements', keys: ['legalAdvisor', 'advisors', 'arrangements'] },
];

// ── Section "not applicable" flags ──────────────────────────────────────
// Persisted as a {sectionKey: true} map on will_status.primary_contact - a
// legacy JSON column with no other purpose in the app today (see recon).
// ALL access to that column goes through these two helpers, so migrating to
// a properly-named column later is a one-line change.
const getSectionFlags = (ws) => {
  const raw = ws?.primary_contact;
  return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
};
const setSectionFlags = (map) => ({ primaryContact: map });

// ── Small presentational helpers ─────────────────────────────────────────
const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const YesNoUnsure = ({ value, onChange }) => (
  <Select value={value || 'unsure'} onValueChange={onChange}>
    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="yes">Yes</SelectItem>
      <SelectItem value="no">No</SelectItem>
      <SelectItem value="unsure">Unsure</SelectItem>
    </SelectContent>
  </Select>
);

const FormFooter = ({ onCancel, onSave, saving }) => (
  <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-border/60">
    <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
      <X className="w-4 h-4 mr-1" />Cancel
    </Button>
    <Button size="sm" onClick={onSave} disabled={saving}>
      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
    </Button>
  </div>
);

// A tile's editor: the exact original form/ListSection JSX, now hosted in a
// dialog instead of an accordion body. onOpenChange(false) routes through the
// same toggle() used everywhere else, so the dirty-discard confirm is unchanged.
const SectionDialog = ({ id, title, open, onOpenChange, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent id={id} className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{title}</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

// ── Generic CRUD list (POA holders, advisors, arrangements, children) ────
const ListSection = ({ items, setItems, api, fields, requiredKey, addLabel, emptyText }) => {
  const [draft, setDraft] = useState({});
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const requiredLabel = fields.find((f) => f.key === requiredKey)?.label || 'This field';
  const hasRequired = (obj) => !!(obj[requiredKey] || '').toString().trim();
  const payloadFrom = (obj) => fields.reduce((p, f) => ({ ...p, [f.key]: obj[f.key] ?? '' }), {});

  const renderInput = (f, obj, set) => {
    if (f.type === 'select') {
      return (
        <Select value={obj[f.key] || ''} onValueChange={(v) => set({ ...obj, [f.key]: v })}>
          <SelectTrigger className="h-9"><SelectValue placeholder={f.label} /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === 'textarea') {
      return <Textarea value={obj[f.key] || ''} onChange={(e) => set({ ...obj, [f.key]: e.target.value })} className="min-h-[60px] resize-none" placeholder={f.label} />;
    }
    return <Input type={f.inputType || 'text'} value={obj[f.key] || ''} onChange={(e) => set({ ...obj, [f.key]: e.target.value })} className="h-9" placeholder={f.label} />;
  };

  const add = async () => {
    if (!hasRequired(draft)) { toast.error(`${requiredLabel} is required`); return; }
    setBusy(true);
    try { const res = await api.create(payloadFrom(draft)); setItems(res.data || []); setDraft({}); }
    catch { toast.error('Could not add'); } finally { setBusy(false); }
  };
  const startEdit = (it) => {
    setEditId(it.id);
    setEdit(fields.reduce((d, f) => ({ ...d, [f.key]: it[f.dataKey || f.key] ?? '' }), {}));
  };
  const saveEdit = async () => {
    if (!hasRequired(edit)) { toast.error(`${requiredLabel} is required`); return; }
    setBusy(true);
    try { const res = await api.update(editId, payloadFrom(edit)); setItems(res.data || []); setEditId(null); }
    catch { toast.error('Could not save'); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    setBusy(true);
    try { const res = await api.delete(id); setItems(res.data || []); }
    catch { toast.error('Could not delete'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      {items.map((it) => (
        <div key={it.id} className="rounded-lg border border-border bg-background p-3">
          {editId === it.id ? (
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {fields.map((f) => (
                  <Field key={f.key} label={f.label} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                    {renderInput(f, edit, setEdit)}
                  </Field>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditId(null)} disabled={busy}>Cancel</Button>
                <Button size="sm" onClick={saveEdit} disabled={busy}>Save</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-sm">
                <p className="font-medium">{it[fields[0].dataKey || fields[0].key] || '-'}</p>
                <p className="text-xs text-muted-foreground">
                  {fields.slice(1).map((f) => it[f.dataKey || f.key]).filter(Boolean).join(' · ') || '-'}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => startEdit(it)}><Edit3 className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {fields.map((f) => (
            <Field key={f.key} label={`${f.label}${f.key === requiredKey ? ' *' : ''}`} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
              {renderInput(f, draft, setDraft)}
            </Field>
          ))}
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={add} disabled={busy}><Plus className="w-4 h-4 mr-1" />{addLabel}</Button>
        </div>
      </div>
    </div>
  );
};

// A content tile: recorded (solid, green tick) / empty (dashed, two actions) / N/A (faded, minus).
const SectionTile = ({ sectionKey, meta, state, preview, onOpen, onMarkNA, onUnmarkNA, flagBusy }) => {
  const Icon = meta.icon;

  if (state === 'na') {
    return (
      <div id={sectionKey} className="rounded-xl border border-border bg-muted/30 p-5 opacity-70" data-testid={`tile-${sectionKey}`}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Minus className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm font-semibold text-foreground">{meta.title}</p>
            <button
              type="button"
              onClick={() => onUnmarkNA(sectionKey)}
              disabled={flagBusy === sectionKey}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              data-testid={`tile-${sectionKey}-change`}
            >
              Not applicable · change
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div id={sectionKey} className="rounded-xl border-2 border-dashed border-border p-5" data-testid={`tile-${sectionKey}`}>
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-foreground">{meta.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Add when you&apos;re ready</p>
            <div className="flex items-center gap-4 mt-3">
              <button
                type="button"
                onClick={() => onOpen(sectionKey)}
                className="text-xs font-medium text-primary hover:underline"
                data-testid={`tile-${sectionKey}-add`}
              >
                Add details
              </button>
              <button
                type="button"
                onClick={() => onMarkNA(sectionKey)}
                disabled={flagBusy === sectionKey}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid={`tile-${sectionKey}-na`}
              >
                Not applicable
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // recorded
  return (
    <button
      type="button"
      id={sectionKey}
      onClick={() => onOpen(sectionKey)}
      className="text-left rounded-xl border border-border bg-card p-5 hover:shadow-md hover:border-primary/30 transition-all"
      data-testid={`tile-${sectionKey}`}
    >
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-heading text-sm font-semibold text-foreground truncate">{meta.title}</p>
            <span className="shrink-0 w-4 h-4 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
              <Check className="w-2.5 h-2.5" strokeWidth={3} />
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{preview}</p>
        </div>
      </div>
    </button>
  );
};

// ── Derive grouped form state from the raw (snake_case) will-status ───────
const obj = (v) => (v && typeof v === 'object' ? v : {});
function deriveForms(ws = {}) {
  return {
    willDetails: {
      willExists: ws.will_exists || 'unsure',
      dateCreated: ws.date_created || '',
      dateUpdated: ws.date_updated || '',
      scope: obj(ws.scope),
      notes: ws.notes || '',
    },
    location: {
      heldWith: ws.held_with || '',
      firmName: ws.firm_name || '',
      location: ws.location || '',
      willHolder: { contactId: '', name: '', email: '', phone: '', firm: '', ...obj(ws.will_holder) },
    },
    executors: {
      executor1: { contactId: '', name: '', relationship: '', email: '', phone: '', ...obj(ws.executor1) },
      executor2: { contactId: '', name: '', relationship: '', email: '', phone: '', ...obj(ws.executor2) },
      executorNotes: ws.executor_notes || '',
    },
    poa: {
      poaExists: ws.poa_exists || 'unsure',
      poaType: ws.poa_type || '',
      poaOtherType: ws.poa_other_type || '',
      poaActivation: ws.poa_activation || '',
      guardianshipExists: ws.guardianship_exists || 'unsure',
      guardianshipNotes: ws.guardianship_notes || '',
      trustsExist: ws.trusts_exist || 'unsure',
      trustsNotes: ws.trusts_notes || '',
    },
    legalAdvisor: { contactId: '', name: '', firm: '', email: '', phone: '', address: '', ...obj(ws.legal_advisor) },
    review: {
      lastReviewedDate: ws.last_reviewed_date || '',
      noChangesAtReview: !!ws.no_changes_at_review,
      reminderDate: ws.reminder_date || '',
      reminderEmail: ws.reminder_email || '',
    },
  };
}

const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};
const childList = (res) => res?.children || res?.data || [];

// ── Main component ───────────────────────────────────────────────────────
const WillStatus = () => {
  const { contacts, getContactById, fetchWillStatus, fetchContacts, isAuthenticated } = useLifely();

  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState(deriveForms());
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(null);
  const [poaHolders, setPoaHolders] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [arrangements, setArrangements] = useState([]);
  const [children, setChildren] = useState([]);
  const [sectionFlags, setSectionFlagsState] = useState({});
  const [flagBusy, setFlagBusy] = useState(null);
  const [showNA, setShowNA] = useState(false);
  const original = useRef(deriveForms());
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [wsRes, poaRes, advRes, arrRes, childRes] = await Promise.all([
          willStatusAPI.get(),
          poaHoldersAPI.list(),
          otherAdvisorsAPI.list(),
          formalArrangementsAPI.list(),
          childrenAPI.list(),
        ]);
        if (cancelled) return;
        const f = deriveForms(wsRes.willStatus || {});
        setForms(f);
        original.current = f;
        setSectionFlagsState(getSectionFlags(wsRes.willStatus));
        setPoaHolders(poaRes.data || []);
        setAdvisors(advRes.data || []);
        setArrangements(arrRes.data || []);
        setChildren(childList(childRes));
        fetchContacts();
      } catch (err) {
        console.error('Failed to load will & legal data:', err);
        if (!cancelled) toast.error('Failed to load your data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Deep-link support: open the section named in the URL hash (e.g.
  // /will-status#executors from the dashboard gap checker) and scroll its
  // tile into view. Waits until data has loaded so the target is in the DOM.
  useEffect(() => {
    if (loading) return;
    const key = (location.hash || '').replace('#', '');
    const VALID = ['willDetails', 'location', 'executors', 'poa', 'poaHolders', 'legalAdvisor', 'advisors', 'arrangements', 'review'];
    if (!VALID.includes(key)) return;
    setExpanded(key);
    requestAnimationFrame(() => {
      document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash, loading]);

  // ── Form helpers ──────────────────────────────────────────────────────
  const setField = (sec, key, value) => setForms((p) => ({ ...p, [sec]: { ...p[sec], [key]: value } }));
  const setLegal = (key, value) => setForms((p) => ({ ...p, legalAdvisor: { ...p.legalAdvisor, [key]: value } }));
  const setPersonField = (sec, personKey, key, value) =>
    setForms((p) => ({ ...p, [sec]: { ...p[sec], [personKey]: { ...p[sec][personKey], [key]: value } } }));
  const pickContact = (apply) => (contactId) => {
    const c = getContactById(contactId);
    apply({ contactId, name: c?.name || '', email: c?.email || '', phone: c?.phone || '' });
  };

  const isDirty = (sec) => JSON.stringify(forms[sec]) !== JSON.stringify(original.current[sec]);

  const toggle = (sec) => {
    if (expanded === sec) {
      if (FORM_SECTIONS.includes(sec) && isDirty(sec)) {
        setForms((p) => ({ ...p, [sec]: original.current[sec] }));
      }
      setExpanded(null);
      return;
    }
    if (expanded && FORM_SECTIONS.includes(expanded) && isDirty(expanded)) {
      if (!window.confirm('Discard unsaved changes to the section you have open?')) return;
      setForms((p) => ({ ...p, [expanded]: original.current[expanded] }));
    }
    setExpanded(sec);
  };

  const cancel = (sec) => {
    setForms((p) => ({ ...p, [sec]: original.current[sec] }));
    setExpanded(null);
  };

  const save = async (sec) => {
    setSaving(sec);
    try {
      const f = forms;
      let payload;
      if (sec === 'willDetails') payload = { willExists: f.willDetails.willExists, dateCreated: f.willDetails.dateCreated, dateUpdated: f.willDetails.dateUpdated, scope: f.willDetails.scope, notes: f.willDetails.notes };
      else if (sec === 'location') payload = { heldWith: f.location.heldWith, firmName: f.location.firmName, location: f.location.location, willHolder: f.location.willHolder };
      else if (sec === 'executors') payload = { executor1: f.executors.executor1, executor2: f.executors.executor2, executorNotes: f.executors.executorNotes };
      else if (sec === 'poa') payload = { poaExists: f.poa.poaExists, poaType: f.poa.poaType, poaOtherType: f.poa.poaOtherType, poaActivation: f.poa.poaActivation, guardianshipExists: f.poa.guardianshipExists, guardianshipNotes: f.poa.guardianshipNotes, trustsExist: f.poa.trustsExist, trustsNotes: f.poa.trustsNotes };
      else if (sec === 'legalAdvisor') { const la = f.legalAdvisor; payload = { legalAdvisor: { contactId: la.contactId, name: la.name, firm: la.firm, email: la.email, phone: la.phone, address: la.address } }; }
      else if (sec === 'review') payload = { lastReviewedDate: f.review.lastReviewedDate, noChangesAtReview: f.review.noChangesAtReview, reminderDate: f.review.reminderDate, reminderEmail: f.review.reminderEmail };

      const res = await willStatusAPI.update(payload);
      const nf = deriveForms(res.willStatus || {});
      setForms(nf);
      original.current = nf;
      setExpanded(null);
      fetchWillStatus(); // keep context (Dashboard etc.) in sync
      toast.success('Saved');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  // Children manager backed by /api/children (re-lists after each change).
  const childApi = {
    create: async (d) => { await childrenAPI.create(d); return { data: childList(await childrenAPI.list()) }; },
    update: async (id, d) => { await childrenAPI.update(id, d); return { data: childList(await childrenAPI.list()) }; },
    delete: async (id) => { await childrenAPI.delete(id); return { data: childList(await childrenAPI.list()) }; },
  };

  // Toggle a section's "not applicable" flag. Goes straight to willStatusAPI
  // (same call save() uses) rather than the LifelyContext helper, matching
  // how this page already talks to will-status directly.
  const toggleNotApplicable = async (key, value) => {
    const next = { ...sectionFlags };
    if (value) next[key] = true; else delete next[key];
    setFlagBusy(key);
    try {
      const res = await willStatusAPI.update(setSectionFlags(next));
      setSectionFlagsState(getSectionFlags(res.willStatus));
      fetchWillStatus();
      toast.success(value ? 'Marked not applicable' : 'Marked applicable again');
    } catch (err) {
      console.error('Failed to update section flag:', err);
      toast.error('Could not update');
    } finally {
      setFlagBusy(null);
    }
  };

  // ── Content state (RECORDED / EMPTY, before the N/A flag is applied) ───
  // Collapses the old three-way Complete/In-progress/Not-set dot into a
  // single boolean: complete-condition OR partial-condition → has content.
  const f = forms;
  const hasContent = {
    willDetails: f.willDetails.willExists !== 'unsure' || !!(f.willDetails.dateCreated || f.willDetails.notes),
    location: !!(f.location.location || f.location.heldWith) || !!(f.location.firmName || f.location.willHolder.name),
    executors: !!f.executors.executor1.name || !!f.executors.executor2.name,
    poa: f.poa.poaExists !== 'unsure' || f.poa.guardianshipExists !== 'unsure' || f.poa.trustsExist !== 'unsure',
    poaHolders: poaHolders.length > 0,
    legalAdvisor: !!f.legalAdvisor.name || !!(f.legalAdvisor.firm || f.legalAdvisor.email),
    advisors: advisors.length > 0,
    arrangements: arrangements.length > 0,
  };
  const tileState = (key) => (sectionFlags[key] ? 'na' : hasContent[key] ? 'recorded' : 'empty');

  const summaries = {
    willDetails: f.willDetails.willExists === 'yes' ? `Will exists${f.willDetails.dateCreated ? ` · created ${fmtDate(f.willDetails.dateCreated)}` : ''}`
      : f.willDetails.willExists === 'no' ? 'No will yet' : 'Details recorded',
    location: f.location.location || HELD_WITH_OPTIONS.find((o) => o.value === f.location.heldWith)?.label || 'Details recorded',
    executors: [f.executors.executor1.name, f.executors.executor2.name].filter(Boolean).join(', ') || 'Details recorded',
    poa: f.poa.poaExists === 'yes' ? `POA exists${f.poa.poaType ? ` · ${POA_TYPE_OPTIONS.find((o) => o.value === f.poa.poaType)?.label || f.poa.poaType}` : ''}` : 'Details recorded',
    poaHolders: `${poaHolders.length} POA holder${poaHolders.length === 1 ? '' : 's'}`,
    legalAdvisor: f.legalAdvisor.name || 'Details recorded',
    advisors: `${advisors.length} advisor${advisors.length === 1 ? '' : 's'}`,
    arrangements: `${arrangements.length} arrangement${arrangements.length === 1 ? '' : 's'}`,
    review: f.review.reminderDate ? `Review reminder set for ${fmtDate(f.review.reminderDate)}` : 'Set a reminder to review your will',
  };

  // ── Progress line ───────────────────────────────────────────────────────
  const naCount = SECTION_ORDER.filter((k) => tileState(k) === 'na').length;
  const recordedCount = SECTION_ORDER.filter((k) => tileState(k) === 'recorded').length;
  const relevantCount = SECTION_ORDER.length - naCount;

  const statusSentence = (() => {
    if (relevantCount === 0) return 'All sections are marked not applicable.';
    if (recordedCount === 0) return 'Start with your will details.';
    if (recordedCount === relevantCount) return 'Your estate details are fully recorded.';
    const r = (k) => tileState(k) === 'recorded';
    if (r('willDetails') && r('executors') && recordedCount <= 3) return 'Your will and executors are in place.';
    if (recordedCount / relevantCount >= 0.6) return 'Your estate details are well recorded.';
    if (r('willDetails')) return 'Your will is recorded - keep going with the rest.';
    return 'A few sections are recorded - keep going.';
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="will-status-page">
      {/* Header */}
      <section className="mb-6 space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">Will and Legal</h1>
        <p className="text-muted-foreground text-lg">Document your formal arrangements and legal wishes.</p>
      </section>

      {/* Progress line */}
      <section className="mb-8 space-y-1.5" data-testid="will-status-progress">
        <p className="text-sm font-medium text-foreground">
          {recordedCount} of {relevantCount} relevant section{relevantCount === 1 ? '' : 's'} recorded
        </p>
        <p className="text-sm text-muted-foreground">{statusSentence}</p>
        {naCount > 0 && (
          <button
            type="button"
            onClick={() => setShowNA((v) => !v)}
            className="inline-flex items-center gap-1 mt-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-700 hover:bg-amber-100 transition-colors"
            data-testid="will-status-na-toggle"
          >
            {naCount} section{naCount === 1 ? '' : 's'} marked not applicable · {showNA ? 'hide' : 'review'}
          </button>
        )}
      </section>

      {/* Grouped tiles */}
      <div className="space-y-8">
        {TILE_GROUPS.map((group) => {
          const visibleKeys = group.keys.filter((k) => showNA || tileState(k) !== 'na');
          if (visibleKeys.length === 0) return null;
          return (
            <section key={group.name} className="space-y-3">
              <h2 className="text-lg font-heading font-medium text-foreground">{group.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {visibleKeys.map((key) => (
                  <SectionTile
                    key={key}
                    sectionKey={key}
                    meta={SECTION_META[key]}
                    state={tileState(key)}
                    preview={summaries[key]}
                    onOpen={toggle}
                    onMarkNA={(k) => toggleNotApplicable(k, true)}
                    onUnmarkNA={(k) => toggleNotApplicable(k, false)}
                    flagBusy={flagBusy}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* ── Section editors (exact original forms, hosted in dialogs) ───── */}

      <SectionDialog id="willDetails-dialog" title="Will Details" open={expanded === 'willDetails'} onOpenChange={(o) => !o && toggle('willDetails')}>
        <div className="space-y-4">
          <Field label="Does a will exist?"><YesNoUnsure value={f.willDetails.willExists} onChange={(v) => setField('willDetails', 'willExists', v)} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date created"><Input type="date" value={f.willDetails.dateCreated} onChange={(e) => setField('willDetails', 'dateCreated', e.target.value)} className="h-9" /></Field>
            <Field label="Date last updated"><Input type="date" value={f.willDetails.dateUpdated} onChange={(e) => setField('willDetails', 'dateUpdated', e.target.value)} className="h-9" /></Field>
          </div>
          <Field label="What the will covers">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[['personalAssets', 'Personal assets'], ['property', 'Property'], ['businessInterests', 'Business interests'], ['digitalAssets', 'Digital assets'], ['other', 'Other']].map(([key, lbl]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={!!f.willDetails.scope[key]} onCheckedChange={(v) => setField('willDetails', 'scope', { ...f.willDetails.scope, [key]: !!v })} />
                  {lbl}
                </label>
              ))}
            </div>
            {f.willDetails.scope.other && (
              <Input value={f.willDetails.scope.otherText || ''} onChange={(e) => setField('willDetails', 'scope', { ...f.willDetails.scope, otherText: e.target.value })} placeholder="Describe other coverage" className="h-9 mt-2" />
            )}
          </Field>
          <Field label="Notes"><Textarea value={f.willDetails.notes} onChange={(e) => setField('willDetails', 'notes', e.target.value)} className="min-h-[70px] resize-none" /></Field>
          <FormFooter onCancel={() => cancel('willDetails')} onSave={() => save('willDetails')} saving={saving === 'willDetails'} />
        </div>
      </SectionDialog>

      <SectionDialog id="location-dialog" title="Will Location &amp; Holder" open={expanded === 'location'} onOpenChange={(o) => !o && toggle('location')}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Held with">
              <Select value={f.location.heldWith} onValueChange={(v) => setField('location', 'heldWith', v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{HELD_WITH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Firm name"><Input value={f.location.firmName} onChange={(e) => setField('location', 'firmName', e.target.value)} className="h-9" /></Field>
          </div>
          <Field label="Location description"><Input value={f.location.location} onChange={(e) => setField('location', 'location', e.target.value)} placeholder="e.g. Safe deposit box at First National Bank" className="h-9" /></Field>
          <div className="space-y-2 pt-2 border-t border-border/60">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Will Holder Contact</p>
            <ContactSelector contacts={contacts} value={f.location.willHolder.contactId} onChange={pickContact((patch) => setForms((p) => ({ ...p, location: { ...p.location, willHolder: { ...p.location.willHolder, ...patch } } })))} placeholder="Link to a contact (optional)..." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name"><Input value={f.location.willHolder.name} onChange={(e) => setPersonField('location', 'willHolder', 'name', e.target.value)} className="h-9" /></Field>
              <Field label="Firm"><Input value={f.location.willHolder.firm} onChange={(e) => setPersonField('location', 'willHolder', 'firm', e.target.value)} className="h-9" /></Field>
              <Field label="Email"><Input type="email" value={f.location.willHolder.email} onChange={(e) => setPersonField('location', 'willHolder', 'email', e.target.value)} className="h-9" /></Field>
              <Field label="Phone"><Input type="tel" value={f.location.willHolder.phone} onChange={(e) => setPersonField('location', 'willHolder', 'phone', e.target.value)} className="h-9" /></Field>
            </div>
          </div>
          <FormFooter onCancel={() => cancel('location')} onSave={() => save('location')} saving={saving === 'location'} />
        </div>
      </SectionDialog>

      <SectionDialog id="executors-dialog" title="Executors" open={expanded === 'executors'} onOpenChange={(o) => !o && toggle('executors')}>
        <div className="space-y-5">
          {[['executor1', 'Primary Executor'], ['executor2', 'Secondary Executor']].map(([key, label]) => {
            const ex = f.executors[key];
            return (
              <div key={key} className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
                <ContactSelector contacts={contacts} value={ex.contactId} onChange={pickContact((patch) => setForms((p) => ({ ...p, executors: { ...p.executors, [key]: { ...p.executors[key], ...patch } } })))} placeholder="Link to a contact (optional)..." />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Name"><Input value={ex.name} onChange={(e) => setPersonField('executors', key, 'name', e.target.value)} className="h-9" /></Field>
                  <Field label="Relationship"><Input value={ex.relationship} onChange={(e) => setPersonField('executors', key, 'relationship', e.target.value)} className="h-9" /></Field>
                  <Field label="Email"><Input type="email" value={ex.email} onChange={(e) => setPersonField('executors', key, 'email', e.target.value)} className="h-9" /></Field>
                  <Field label="Phone"><Input type="tel" value={ex.phone} onChange={(e) => setPersonField('executors', key, 'phone', e.target.value)} className="h-9" /></Field>
                </div>
              </div>
            );
          })}
          <Field label="Executor notes"><Textarea value={f.executors.executorNotes} onChange={(e) => setField('executors', 'executorNotes', e.target.value)} className="min-h-[60px] resize-none" /></Field>
          <FormFooter onCancel={() => cancel('executors')} onSave={() => save('executors')} saving={saving === 'executors'} />
        </div>
      </SectionDialog>

      <SectionDialog id="poa-dialog" title="Power of Attorney" open={expanded === 'poa'} onOpenChange={(o) => !o && toggle('poa')}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Does a POA exist?"><YesNoUnsure value={f.poa.poaExists} onChange={(v) => setField('poa', 'poaExists', v)} /></Field>
            <Field label="POA type">
              <Select value={f.poa.poaType} onValueChange={(v) => setField('poa', 'poaType', v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{POA_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {f.poa.poaType === 'other' && (
            <Field label="Other type"><Input value={f.poa.poaOtherType} onChange={(e) => setField('poa', 'poaOtherType', e.target.value)} className="h-9" /></Field>
          )}
          <Field label="Activation conditions"><Input value={f.poa.poaActivation} onChange={(e) => setField('poa', 'poaActivation', e.target.value)} placeholder="e.g. On incapacity" className="h-9" /></Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border/60">
            <Field label="Guardianship arrangements exist?"><YesNoUnsure value={f.poa.guardianshipExists} onChange={(v) => setField('poa', 'guardianshipExists', v)} /></Field>
            <Field label="Trusts exist?"><YesNoUnsure value={f.poa.trustsExist} onChange={(v) => setField('poa', 'trustsExist', v)} /></Field>
          </div>
          <Field label="Guardianship notes"><Textarea value={f.poa.guardianshipNotes} onChange={(e) => setField('poa', 'guardianshipNotes', e.target.value)} className="min-h-[50px] resize-none" /></Field>
          <Field label="Trusts notes"><Textarea value={f.poa.trustsNotes} onChange={(e) => setField('poa', 'trustsNotes', e.target.value)} className="min-h-[50px] resize-none" /></Field>

          <FormFooter onCancel={() => cancel('poa')} onSave={() => save('poa')} saving={saving === 'poa'} />

          {/* Children sub-area (saved independently via /api/children) - stays nested exactly as before */}
          <div className="pt-4 mt-2 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Children &amp; Dependents</p>
            <ListSection
              items={children} setItems={setChildren} api={childApi} requiredKey="name" addLabel="Add child"
              emptyText="No children recorded."
              fields={[
                { key: 'name', label: 'Name' },
                { key: 'dateOfBirth', label: 'Date of birth', dataKey: 'date_of_birth', inputType: 'date' },
                { key: 'intendedGuardianName', label: 'Intended guardian', dataKey: 'intended_guardian_name' },
                { key: 'notes', label: 'Notes', type: 'textarea' },
              ]}
            />
          </div>
        </div>
      </SectionDialog>

      <SectionDialog id="poaHolders-dialog" title="POA Holders" open={expanded === 'poaHolders'} onOpenChange={(o) => !o && toggle('poaHolders')}>
        <ListSection
          items={poaHolders} setItems={setPoaHolders} api={poaHoldersAPI} requiredKey="name" addLabel="Add POA holder"
          emptyText="No POA holders added yet."
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'role', label: 'Role' },
            { key: 'email', label: 'Email', inputType: 'email' },
            { key: 'phone', label: 'Phone', inputType: 'tel' },
          ]}
        />
      </SectionDialog>

      <SectionDialog id="legalAdvisor-dialog" title="Legal Advisor" open={expanded === 'legalAdvisor'} onOpenChange={(o) => !o && toggle('legalAdvisor')}>
        <div className="space-y-3">
          <ContactSelector contacts={contacts} value={f.legalAdvisor.contactId} onChange={pickContact((patch) => setForms((p) => ({ ...p, legalAdvisor: { ...p.legalAdvisor, ...patch } })))} placeholder="Link to a contact (optional)..." />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name"><Input value={f.legalAdvisor.name} onChange={(e) => setLegal('name', e.target.value)} className="h-9" /></Field>
            <Field label="Firm"><Input value={f.legalAdvisor.firm} onChange={(e) => setLegal('firm', e.target.value)} className="h-9" /></Field>
            <Field label="Email"><Input type="email" value={f.legalAdvisor.email} onChange={(e) => setLegal('email', e.target.value)} className="h-9" /></Field>
            <Field label="Phone"><Input type="tel" value={f.legalAdvisor.phone} onChange={(e) => setLegal('phone', e.target.value)} className="h-9" /></Field>
            <Field label="Address" className="sm:col-span-2"><Input value={f.legalAdvisor.address} onChange={(e) => setLegal('address', e.target.value)} className="h-9" /></Field>
          </div>
          <FormFooter onCancel={() => cancel('legalAdvisor')} onSave={() => save('legalAdvisor')} saving={saving === 'legalAdvisor'} />
        </div>
      </SectionDialog>

      <SectionDialog id="advisors-dialog" title="Other Advisors" open={expanded === 'advisors'} onOpenChange={(o) => !o && toggle('advisors')}>
        <ListSection
          items={advisors} setItems={setAdvisors} api={otherAdvisorsAPI} requiredKey="name" addLabel="Add advisor"
          emptyText="No advisors added yet."
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'type', label: 'Type', type: 'select', options: ADVISOR_TYPE_OPTIONS },
            { key: 'email', label: 'Email', inputType: 'email' },
            { key: 'phone', label: 'Phone', inputType: 'tel' },
          ]}
        />
      </SectionDialog>

      <SectionDialog id="arrangements-dialog" title="Formal Arrangements" open={expanded === 'arrangements'} onOpenChange={(o) => !o && toggle('arrangements')}>
        <ListSection
          items={arrangements} setItems={setArrangements} api={formalArrangementsAPI} requiredKey="type" addLabel="Add arrangement"
          emptyText="No arrangements documented yet."
          fields={[
            { key: 'type', label: 'Document type' },
            { key: 'heldWith', label: 'Held with', dataKey: 'held_with' },
            { key: 'notes', label: 'Notes', type: 'textarea' },
          ]}
        />
      </SectionDialog>

      <SectionDialog id="review-dialog" title="Review Reminder" open={expanded === 'review'} onOpenChange={(o) => !o && toggle('review')}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Last reviewed"><Input type="date" value={f.review.lastReviewedDate} onChange={(e) => setField('review', 'lastReviewedDate', e.target.value)} className="h-9" /></Field>
            <Field label="Reminder date"><Input type="date" value={f.review.reminderDate} onChange={(e) => setField('review', 'reminderDate', e.target.value)} className="h-9" /></Field>
          </div>
          <Field label="Reminder email"><Input type="email" value={f.review.reminderEmail} onChange={(e) => setField('review', 'reminderEmail', e.target.value)} placeholder="Where to send the review reminder" className="h-9" /></Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={f.review.noChangesAtReview} onCheckedChange={(v) => setField('review', 'noChangesAtReview', !!v)} />
            No changes were needed at last review
          </label>
          <FormFooter onCancel={() => cancel('review')} onSave={() => save('review')} saving={saving === 'review'} />
        </div>
      </SectionDialog>

      {/* Review strip + notice - review is maintenance, not a content tile, so it sits
          outside the grid and isn't counted in the fraction above. */}
      <div id="review" className="mt-10 space-y-3">
        <button
          type="button"
          onClick={() => toggle('review')}
          className="w-full flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-4 text-left hover:bg-muted/40 transition-colors"
          data-testid="review-strip"
        >
          <div className="p-2 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Bell className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Review reminder</p>
            <p className="text-xs text-muted-foreground truncate">{summaries.review}</p>
          </div>
          <span className="text-xs font-medium text-primary shrink-0">Edit</span>
        </button>

        <Card className="border border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Note:</span> Lifely does not store legal documents or provide legal advice.
              This section keeps your formal arrangements visible and locatable when needed.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WillStatus;
